import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { setAddon, setBand, type ManageSubscriptionResult } from '@/lib/manageSubscription';

/**
 * Gemeinsame Mutations fuer die Edge Function `manage-subscription`
 * (Bandwechsel + Add-ons fuer Bestandskunden). Von der Preisseite UND der
 * Settings-Plan-Karte benutzt, damit beide Flaechen identisch invalidieren:
 * `subscription` (Plan-Karte, Add-on-Zaehler) und `enforcement`
 * (`check_can_optimize` — das neue `product_limit` soll ohne Reload greifen).
 *
 * Fehlerbehandlung (Toast vs. Checkout-Fallback bei 409) bleibt beim Aufrufer —
 * die Preisseite leitet bei `NO_ACTIVE_SUBSCRIPTION` in den Checkout um, die
 * Settings-Karte zeigt stattdessen den "erst Band waehlen"-Hinweis.
 */

/**
 * Session-lokaler Merker fuer einen zum Periodenende GEPLANTEN Bandwechsel.
 *
 * Bewusste Grenze (MAJOR-3b): der pendende Schedule lebt NUR bei Stripe — die
 * DB spiegelt ihn nicht, also weiss der Client nur nach eigener Aktion davon.
 * Der Merker liegt deshalb im React-Query-Cache und ist nach einem Reload weg.
 * Die Server-Wahrheit bleibt Stripe: nach dem Reload deckt der 409
 * SCHEDULE_PENDING der naechsten Aenderung den Fall ab (PlanBandManager bietet
 * dort den Abbruch-Pfad erneut an).
 */
export type PendingBandChange = {
  /** Lookup-Key des Ziel-Bands (z.B. `cap500_monthly`). */
  targetLookup: string;
  /** ISO-Datum des Periodenendes, falls der Server eins nannte. */
  effectiveAt?: string;
};

export const pendingBandChangeKey = (userId: string | undefined) =>
  ['pending-band-change', userId] as const;

/** Liest den session-lokalen Merker reaktiv (null = kein bekannter Schedule). */
export function usePendingBandChange(): PendingBandChange | null {
  const { user } = useAuth();
  const { data } = useQuery<PendingBandChange | null>({
    queryKey: pendingBandChangeKey(user?.id),
    // Es gibt nichts zu fetchen — geschrieben wird ausschliesslich per
    // setQueryData in useSetBandMutation. enabled:false haelt die Query
    // passiv, der Observer bekommt Cache-Schreibzugriffe trotzdem mit.
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? null;
}

function useInvalidatePlanQueries() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['enforcement', user?.id] });
  };
}

export function useSetBandMutation() {
  const invalidate = useInvalidatePlanQueries();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation<ManageSubscriptionResult, unknown, string>({
    mutationFn: (lookupKey: string) => setBand(lookupKey),
    onSuccess: (result, lookupKey) => {
      invalidate();
      if (result.status === 'scheduled') {
        queryClient.setQueryData<PendingBandChange | null>(pendingBandChangeKey(user?.id), {
          targetLookup: lookupKey,
          effectiveAt: result.effective_at,
        });
      } else {
        // 'updated' (Sofort-Wechsel ersetzt jeden alten Plan samt Schedule)
        // und 'released' (expliziter Abbruch) raeumen den Merker ab.
        queryClient.setQueryData<PendingBandChange | null>(pendingBandChangeKey(user?.id), null);
      }
    },
  });
}

export function useSetAddonMutation() {
  const invalidate = useInvalidatePlanQueries();
  return useMutation<ManageSubscriptionResult, unknown, { addon: 'mc' | 'language'; quantity: number }>({
    mutationFn: ({ addon, quantity }) => setAddon(addon, quantity),
    onSuccess: invalidate,
  });
}
